/**
 * A remote shell in the browser (stage 2): xterm.js on the session's
 * WebSocket.
 *
 * Framing, as the relay and the agent agree (src/remote/relay.ts,
 * cosmon-agent/internal/remote/shell.go): binary frames are the terminal —
 * keystrokes out, screen in — and text frames are JSON control: `resize` from
 * here, `hello` and `exit` from the host.
 */

import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { useEffect, useRef } from 'react';

export interface OpenedSession {
  id: string;
  path: string;
  protocol: string;
  ticket: string;
}

/**
 * The session's socket, opened ONCE by whoever pressed Connect.
 *
 * 🔴 Not opened by the terminal. The ticket is single-use, and a component
 * that opens its socket on mount opens it twice under React's development
 * double-mount — the first close reads as the browser leaving and the second
 * open is refused. Found by the e2e spec on its first shell run. Whatever
 * arrives before a terminal is attached waits in `queue`.
 */
export interface ShellSocket {
  ws: WebSocket;
  queue: MessageEvent[];
}

export function openShellSocket(session: OpenedSession): ShellSocket {
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // The ticket rides as a second subprotocol, never in the URL (see src/remote/policy.ts).
  const ws = new WebSocket(`${scheme}://${window.location.host}${session.path}`, [session.protocol, `ticket.${session.ticket}`]);
  ws.binaryType = 'arraybuffer';
  const socket: ShellSocket = { ws, queue: [] };
  ws.onmessage = (e) => socket.queue.push(e);
  return socket;
}

export function ShellTerminal({
  socket,
  onStatus,
}: {
  socket: ShellSocket;
  onStatus: (text: string) => void;
}): React.JSX.Element {
  const box = useRef<HTMLDivElement>(null);
  const status = useRef(onStatus);
  status.current = onStatus;

  useEffect(() => {
    const el = box.current;
    if (el === null) return undefined;
    const { ws } = socket;
    // A terminal is dark in both themes: programs colour their output for a dark ground.
    const term = new Terminal({
      cursorBlink: true,
      fontFamily: getComputedStyle(document.documentElement).getPropertyValue('--mono').trim() || 'monospace',
      fontSize: 13,
      scrollback: 5000,
      theme: { background: '#0d1117', foreground: '#e6edf3', cursor: '#e6edf3' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(el);
    fit.fit();

    const sendSize = (): void => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    };
    const onMessage = (e: MessageEvent<ArrayBuffer | string>): void => {
      if (typeof e.data !== 'string') {
        term.write(new Uint8Array(e.data));
        return;
      }
      try {
        const m = JSON.parse(e.data) as { type?: string; host?: string; os?: string; shell?: string; version?: string; code?: number };
        if (m.type === 'hello') status.current(`Shell on ${m.host ?? '?'} (${m.os ?? '?'}, ${m.shell ?? 'shell'}, agent ${m.version ?? '?'}). Recorded.`);
        if (m.type === 'exit') status.current(`The shell exited (status ${m.code ?? '?'}).`);
      } catch {
        // Not control: nothing a host sends as text is terminal output.
      }
    };
    for (const e of socket.queue.splice(0)) onMessage(e as MessageEvent<ArrayBuffer | string>);
    ws.onmessage = onMessage;
    ws.onclose = (e) => status.current(`Session closed${e.reason === '' ? '' : `: ${e.reason}`}.`);
    if (ws.readyState === WebSocket.OPEN) sendSize();
    else {
      status.current('Connecting to the relay…');
      ws.addEventListener('open', () => {
        status.current('Connected to the relay. Waiting for the host…');
        sendSize();
      }, { once: true });
    }

    const encoder = new TextEncoder();
    const typed = term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
    });
    const resized = term.onResize(sendSize);
    const observer = new ResizeObserver(() => fit.fit());
    observer.observe(el);
    term.focus();

    return () => {
      typed.dispose();
      resized.dispose();
      observer.disconnect();
      // Detach, never close: closing is the drawer's, when the person ends it.
      ws.onmessage = (e) => socket.queue.push(e);
      term.dispose();
    };
  }, [socket]);

  return <div ref={box} className="remote-terminal" role="application" aria-label="Remote shell" />;
}
